import {
  type PromotionState,
  type PromotionWindow,
  parseReleaseInstant,
  promotionStateAt,
} from './promotion';

/**
 * Where the game is in its life, and what that authorises.
 *
 * Three stages, in order, each ending in a total reset of everything except
 * what was PAID for:
 *
 * - `closed_alpha` - 30-50 invited players, half price, ends in a reset into
 *   the open alpha, which returns 100% of the Cash bought here;
 * - `open_alpha` - public, half price, ends in a reset into the official
 *   version, which returns 130% of the Cash bought across BOTH alphas;
 * - `official` - no announced reset, no alpha promotion, normal prices, and an
 *   economy that is meant to be permanent.
 *
 * ## The stage is CONFIGURED, never inferred
 *
 * It used to be derived from whether a discount was running, which conflated
 * two different facts: "half price is authorised right now" and "which game
 * this is". A promotion that had not started yet made the shop believe the
 * alpha was over. So the stage is its own variable, the promotion is a window
 * inside it, and neither is computed from the other.
 *
 * In development and test an unset stage means `closed_alpha`, because that is
 * what a developer's machine is. In PRODUCTION it must be stated: shipping a
 * live economy that quietly believes it is an alpha - and promises a reset it
 * will not perform - is worse than refusing to start.
 *
 * ## A browser never decides any of this
 *
 * The stage, the windows and the price all travel from the server already
 * resolved. The interface renders a countdown; it never works out what
 * anything costs or which warning applies.
 */

export const RELEASE_STAGES = ['closed_alpha', 'open_alpha', 'official'] as const;
export type ReleaseStage = (typeof RELEASE_STAGES)[number];

/**
 * The stage a machine falls back to when nothing says otherwise.
 *
 * Only outside production. It is the safe direction: an alpha warns about a
 * reset that may not happen, while an unannounced `official` would hide one
 * that will.
 */
export const DEFAULT_RELEASE_STAGE: ReleaseStage = 'closed_alpha';

/** Parses the configured stage, or answers null. Never guesses. */
export function parseReleaseStage(value: string | undefined | null): ReleaseStage | null {
  if (typeof value !== 'string') {
    return null;
  }
  const text = value.trim();
  return (RELEASE_STAGES as readonly string[]).includes(text) ? (text as ReleaseStage) : null;
}

/** The two promotional windows, each configured independently. */
export interface ReleaseWindows {
  readonly closedAlpha: PromotionWindow;
  readonly openAlpha: PromotionWindow;
}

export interface ReleaseConfig {
  readonly stage: ReleaseStage;
  readonly windows: ReleaseWindows;
}

/** An empty window. Absent means NO promotion, never a guessed one. */
export const NO_PROMOTION: PromotionWindow = Object.freeze({ startsAt: null, endsAt: null });

/**
 * Reads the four instants, refusing anything ambiguous.
 *
 * Each has to be a full instant with an offset - `2026-08-20T23:59:59-03:00`,
 * not `2026-08-20T23:59:59`, which is a wish about somebody's local time and
 * would mean different things on a laptop in São Paulo and a Worker in
 * Frankfurt. Unparseable is treated as ABSENT, and absent means no promotion.
 */
export function parseReleaseWindows(env: {
  readonly CLOSED_ALPHA_START_AT?: string | undefined;
  readonly CLOSED_ALPHA_END_AT?: string | undefined;
  readonly OPEN_ALPHA_START_AT?: string | undefined;
  readonly OPEN_ALPHA_END_AT?: string | undefined;
}): ReleaseWindows {
  return {
    closedAlpha: {
      startsAt: parseReleaseInstant(env.CLOSED_ALPHA_START_AT),
      endsAt: parseReleaseInstant(env.CLOSED_ALPHA_END_AT),
    },
    openAlpha: {
      startsAt: parseReleaseInstant(env.OPEN_ALPHA_START_AT),
      endsAt: parseReleaseInstant(env.OPEN_ALPHA_END_AT),
    },
  };
}

/**
 * Everything wrong with a pair of windows, as reasons.
 *
 * Returned rather than thrown, so the service can name each problem while
 * refusing to start. Two rules, and both exist because breaking either would
 * charge somebody the wrong amount:
 *
 * - a window that ends before it starts is not a window;
 * - the two must not OVERLAP. A single instant belonging to both alphas would
 *   make "which discount applies" a question with two answers, and the stage -
 *   which is configured separately - could disagree with either.
 */
export function releaseWindowProblems(windows: ReleaseWindows): readonly string[] {
  const problems: string[] = [];
  const named = [
    ['CLOSED_ALPHA', windows.closedAlpha],
    ['OPEN_ALPHA', windows.openAlpha],
  ] as const;

  for (const [name, window] of named) {
    if (window.startsAt && window.endsAt && window.startsAt.getTime() >= window.endsAt.getTime()) {
      problems.push(`${name}_START_AT is not before ${name}_END_AT`);
    }
  }

  /*
   * The closed alpha comes first, and the two never share an instant.
   *
   * Touching is fine - the open alpha may begin at the exact instant the closed
   * one ends - because both windows are half-open: inclusive of their start and
   * EXCLUSIVE of their end. So `open.startsAt === closed.endsAt` belongs to the
   * open alpha alone, and only an earlier start is an overlap.
   */
  const closed = windows.closedAlpha;
  const open = windows.openAlpha;
  if (closed.endsAt && open.startsAt && open.startsAt.getTime() < closed.endsAt.getTime()) {
    problems.push('OPEN_ALPHA_START_AT is before CLOSED_ALPHA_END_AT: the windows overlap');
  }
  if (closed.startsAt && open.startsAt && closed.startsAt.getTime() >= open.startsAt.getTime()) {
    problems.push(
      'CLOSED_ALPHA_START_AT is not before OPEN_ALPHA_START_AT: the stages are out of order',
    );
  }
  return problems;
}

/**
 * Which window a stage may discount from.
 *
 * `official` has NONE, and that is structural rather than a check somebody has
 * to remember: there is no expression here that could return the alpha's window
 * for it, so a leftover `CLOSED_ALPHA_END_AT` on a production machine cannot
 * quietly resurrect a promotion after launch.
 */
export function promotionWindowFor(stage: ReleaseStage, windows: ReleaseWindows): PromotionWindow {
  if (stage === 'closed_alpha') {
    return windows.closedAlpha;
  }
  if (stage === 'open_alpha') {
    return windows.openAlpha;
  }
  return NO_PROMOTION;
}

export interface ReleaseState {
  readonly stage: ReleaseStage;
  /** The promotion of THIS stage, resolved from the server's own clock. */
  readonly promotion: PromotionState;
}

/** The stage and its promotion, as of an instant. The one place both are read. */
export function releaseStateAt(config: ReleaseConfig, now: Date): ReleaseState {
  return {
    stage: config.stage,
    promotion: promotionStateAt(promotionWindowFor(config.stage, config.windows), now),
  };
}

/* -------------------------------------------------------------------------- */
/*  Transitions                                                               */
/* -------------------------------------------------------------------------- */

/**
 * The two resets, named by where they LAND.
 *
 * `official` is not a destination anything transitions out of: it is the end of
 * the sequence, and there is no announced reset after it.
 */
export const RELEASE_TRANSITIONS = ['open_alpha', 'official'] as const;
export type ReleaseTransition = (typeof RELEASE_TRANSITIONS)[number];

/** The stage a transition leaves. Fixed by the sequence, never configured. */
export function stageBefore(destination: ReleaseTransition): ReleaseStage {
  return destination === 'open_alpha' ? 'closed_alpha' : 'open_alpha';
}

/**
 * Which purchases fund a transition's credit.
 *
 * The open alpha returns what was bought in the closed alpha. The official
 * launch returns what was bought across BOTH - which is why a purchase made in
 * the closed alpha appears in both bases, at 100% then at 130%, and is not
 * double-counted in either: they are two different resets, each crediting once.
 *
 * A purchase made in `official` funds nothing, because there is no reset after
 * it to be compensated for.
 */
export function stagesFunding(destination: ReleaseTransition): readonly ReleaseStage[] {
  return destination === 'open_alpha'
    ? (['closed_alpha'] as const)
    : (['closed_alpha', 'open_alpha'] as const);
}
